import { useCallback, useMemo } from 'react';
import { useLazyGetPreflightByIdQuery } from '../../../api';

export const useSelectionAndActions = ({
    tableData,
    expandedRowKeys,
    setExpandedRowKeys,
    effectiveSelectedRowKeys,
    setEffectiveSelectedRowKeys,
}) => {
    const [triggerGetPreflight] = useLazyGetPreflightByIdQuery();
    const onSelectChange = useCallback(
        (newSelectedRowKeys, selectedRows) => {
            const currentSelected = new Set(effectiveSelectedRowKeys);
            const newSelected = new Set(newSelectedRowKeys);

            const added = newSelectedRowKeys.filter((key) => !currentSelected.has(key));
            const removed = effectiveSelectedRowKeys.filter((key) => !newSelected.has(key));

            let finalKeys = [...newSelectedRowKeys];

            added.forEach((addedKey) => {
                const addedItem = tableData.find((item) => item.key === addedKey);
                if (addedItem?.isGroup && addedItem.children) {
                    addedItem.children.forEach((child) => {
                        if (!finalKeys.includes(child.key)) {
                            finalKeys.push(child.key);
                        }
                    });
                }
            });

            removed.forEach((removedKey) => {
                const removedItem = tableData.find((item) => item.key === removedKey);
                if (removedItem?.isGroup && removedItem.children) {
                    removedItem.children.forEach((child) => {
                        const childIndex = finalKeys.indexOf(child.key);
                        if (childIndex > -1) {
                            finalKeys.splice(childIndex, 1);
                        }
                    });
                } else if (removedItem && !removedItem.isGroup) {
                    const parentGroup = tableData.find(
                        (group) => group.isGroup && group.children?.some((child) => child.key === removedKey),
                    );
                    if (parentGroup && finalKeys.includes(parentGroup.key)) {
                        const groupIndex = finalKeys.indexOf(parentGroup.key);
                        if (groupIndex > -1) {
                            finalKeys.splice(groupIndex, 1);
                        }
                    }
                }
            });

            tableData.forEach((group) => {
                if (group.isGroup && group.children) {
                    const allChildrenSelected = group.children.every((child) => finalKeys.includes(child.key));
                    const isGroupSelected = finalKeys.includes(group.key);

                    if (allChildrenSelected && !isGroupSelected) {
                        finalKeys.push(group.key);
                    } else if (!allChildrenSelected && isGroupSelected) {
                        const groupIndex = finalKeys.indexOf(group.key);
                        if (groupIndex > -1) {
                            finalKeys.splice(groupIndex, 1);
                        }
                    }
                }
            });

            setEffectiveSelectedRowKeys(finalKeys);
        },
        [tableData, effectiveSelectedRowKeys, setEffectiveSelectedRowKeys],
    );

    const handleRowClick = useCallback(
        (e, record, setSelectedSourceId, setDrawerOpen, setSourcePreflightData) => {
            if (!record.isGroup && record.id) {
                setSelectedSourceId(record.id);
                setDrawerOpen(true);

                // Fetch preflight details if source has error/warning state
                if (record.preflight_state === 'error') {
                    triggerGetPreflight(record.preflight_id)
                        .then((result) => {
                            if (result.data && setSourcePreflightData) {
                                setSourcePreflightData(result.data);
                            }
                        })
                        .catch((error) => {
                            console.error('Failed to fetch preflight details:', error);
                            if (setSourcePreflightData) {
                                setSourcePreflightData({ error: true });
                            }
                        });
                } else if (setSourcePreflightData) {
                    // Clear preflight data if source doesn't have errors/warnings
                    setSourcePreflightData(null);
                }
            } else {
                e.stopPropagation();
                const newExpandedKeys = expandedRowKeys.includes(record.key)
                    ? expandedRowKeys.filter((key) => key !== record.key)
                    : [...expandedRowKeys, record.key];
                setExpandedRowKeys(newExpandedKeys);
            }
        },
        [expandedRowKeys, setExpandedRowKeys, triggerGetPreflight],
    );

    const rowSelection = useMemo(
        () => ({
            selectedRowKeys: effectiveSelectedRowKeys,
            onChange: onSelectChange,
            getCheckboxProps: (record) => ({ name: record.key }),
            columnWidth: 60,
            onCell: () => ({
                className: 'checkbox-cell-clickable',
                onClick: (e) => {
                    e.stopPropagation();
                },
            }),
        }),
        [effectiveSelectedRowKeys, onSelectChange],
    );

    return { onSelectChange, handleRowClick, rowSelection };
};
